import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { StatusPill } from "@/components/StatusPill";
import { fmtDate } from "@/lib/constants";
import { Plus, Save, Trash2, GitCommit, Sparkles, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// Festes Rollen-Vokabular (siehe Prompt-Studio-Spec, Abschnitt 4)
const PROMPT_ROLES = [
  { key: "north_star", label: "North-Star" },
  { key: "discuss_first", label: "Discuss-First" },
  { key: "foundation", label: "Foundation" },
  { key: "module", label: "Module" },
  { key: "change", label: "Change" },
  { key: "review", label: "Review" },
  { key: "hardening", label: "Hardening" },
];

const newPrompt = () => ({ name: "", purpose: "", role: "foundation", content: "", order: 0, model_provider: "", model_name: "", expected_output: "", active: true });

export default function PromptStudio() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [sets, setSets] = useState([]);
  const [discoveryPacks, setDiscoveryPacks] = useState([]);
  const [active, setActive] = useState(null);          // current set
  const [activeVersion, setActiveVersion] = useState(null);
  const [edit, setEdit] = useState({ name: "", description: "", einsatzkontext: "", uebergabe_an_build: "", discovery_pack_id: "", discovery_pack_version: "", prompts: [] });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      const url = new URL(window.location.href);
      setProjectId(url.searchParams.get("project") || r.data[0]?.project_id || "");
    });
  }, []);

  const reload = async (id) => {
    if (!id) return;
    const [s, d] = await Promise.all([
      api.get(`/projects/${id}/prompts`),
      api.get(`/projects/${id}/discovery`),
    ]);
    setSets(s.data);
    setDiscoveryPacks(d.data);
    if (s.data[0]) selectSet(s.data[0]);
    else { setActive(null); setActiveVersion(null); setEdit({ name: "", description: "", einsatzkontext: "", uebergabe_an_build: "", discovery_pack_id: "", discovery_pack_version: "", prompts: [] }); }
  };
  useEffect(() => { reload(projectId); /* eslint-disable-next-line */ }, [projectId]);

  const selectSet = (s) => {
    setActive(s);
    const v = s.versions.find((x) => x.version === s.current_version) || s.versions[s.versions.length - 1];
    setActiveVersion(v);
    setEdit({
      name: s.name, description: s.description || "",
      einsatzkontext: v?.einsatzkontext || "",
      uebergabe_an_build: v?.uebergabe_an_build || "",
      discovery_pack_id: v?.discovery_pack_id || "",
      discovery_pack_version: v?.discovery_pack_version ?? "",
      prompts: (v?.prompts || []).map((p) => ({ ...newPrompt(), ...p })),
    });
    setCreating(false);
  };

  const startCreate = () => {
    setActive(null); setActiveVersion(null);
    const closedDiscoveries = discoveryPacks.filter((d) => d.status === "abgeschlossen");
    const def = closedDiscoveries[0];
    setEdit({
      name: "Neues Prompt Set",
      description: "",
      einsatzkontext: "",
      uebergabe_an_build: "",
      discovery_pack_id: def?.pack_id || "",
      discovery_pack_version: def?.version ?? "",
      prompts: [{ ...newPrompt(), name: "Foundation: Domain-Kontext", role: "foundation" }],
    });
    setCreating(true);
  };

  const frozen = activeVersion?.status === "abgeschlossen" || activeVersion?.status === "abgelehnt";

  const addPrompt = () => setEdit({ ...edit, prompts: [...edit.prompts, { ...newPrompt(), order: edit.prompts.length, role: "module" }] });
  const removePrompt = (i) => setEdit({ ...edit, prompts: edit.prompts.filter((_, idx) => idx !== i) });
  const updatePrompt = (i, patch) => setEdit({ ...edit, prompts: edit.prompts.map((p, idx) => idx === i ? { ...p, ...patch } : p) });

  const buildPayload = () => ({
    name: edit.name,
    description: edit.description,
    einsatzkontext: edit.einsatzkontext,
    uebergabe_an_build: edit.uebergabe_an_build,
    discovery_pack_id: edit.discovery_pack_id || null,
    discovery_pack_version: edit.discovery_pack_version === "" ? null : Number(edit.discovery_pack_version),
    prompts: edit.prompts.map((p, idx) => ({ ...p, order: idx })),
  });

  const save = async (createVersion = false) => {
    if (!projectId) return;
    try {
      if (creating) {
        const r = await api.post(`/projects/${projectId}/prompts`, buildPayload());
        toast.success("Prompt Set angelegt");
        await reload(projectId);
        selectSet(r.data);
      } else if (active) {
        const r = await api.patch(`/projects/${projectId}/prompts/${active.set_id}`, { ...buildPayload(), create_version: createVersion });
        toast.success(createVersion ? `Version v${r.data.current_version} angelegt` : "Aktuelle Version aktualisiert");
        await reload(projectId);
        selectSet(r.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const closeVersion = async (newStatus) => {
    if (!active || !activeVersion) return;
    try {
      await api.post(`/projects/${projectId}/prompts/${active.set_id}/versions/${activeVersion.version}/status`, { status: newStatus });
      toast.success(`v${activeVersion.version} ${newStatus}`);
      reload(projectId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Statuswechsel fehlgeschlagen");
    }
  };

  const remove = async () => {
    if (!active || !window.confirm("Set löschen?")) return;
    await api.delete(`/projects/${projectId}/prompts/${active.set_id}`);
    toast.success("Prompt Set gelöscht");
    reload(projectId);
  };

  const closedDiscoveries = useMemo(() => discoveryPacks.filter((d) => d.status === "abgeschlossen"), [discoveryPacks]);

  return (
    <InternalLayout>
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Phase 2</div>
        <h1 className="font-display text-3xl md:text-4xl text-neutral-900 flex items-center gap-3">
          Prompt Studio <Sparkles className="w-6 h-6 text-[#183153]" />
        </h1>
        <p className="text-sm text-neutral-600 mt-2">Versionierte Prompts, gebunden an eine abgeschlossene Discovery-Pack-Version. Eingefrorene Versionen sind read-only.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="prompts-project-select" className="px-3 py-2 border border-neutral-300 rounded-sm text-sm">
          <option value="">Projekt wählen…</option>
          {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
        </select>
        <button onClick={startCreate} disabled={!projectId || closedDiscoveries.length === 0} data-testid="prompts-new-set-button" className="inline-flex items-center gap-2 px-3 py-2 text-sm text-white rounded-sm disabled:opacity-50" style={{ background: "#183153" }}>
          <Plus className="w-4 h-4" /> Neues Set
        </button>
        {projectId && closedDiscoveries.length === 0 && (
          <span className="text-xs text-amber-700">Erst eine Discovery-Pack-Version <strong>abschließen</strong>, bevor ein Prompt Set angelegt werden kann.</span>
        )}
      </div>

      <div className="grid grid-cols-12 gap-px bg-neutral-200 border border-neutral-200">
        <div className="col-span-12 md:col-span-3 bg-white">
          <div className="border-b border-neutral-200 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Prompt Sets</div>
          <div className="divide-y divide-neutral-100 max-h-[60vh] overflow-y-auto">
            {sets.length === 0 && <div className="px-4 py-6 text-sm text-neutral-500">Keine Sets.</div>}
            {sets.map((s) => {
              const v = s.versions.find((x) => x.version === s.current_version);
              return (
                <button
                  key={s.set_id}
                  onClick={() => selectSet(s)}
                  data-testid={`prompt-set-${s.set_id}`}
                  className={`w-full text-left px-4 py-3 hover:bg-neutral-50 ${active?.set_id === s.set_id ? "bg-neutral-50 border-l-2 border-l-[#183153]" : ""}`}
                >
                  <div className="text-sm font-medium text-neutral-900 truncate">{s.name}</div>
                  <div className="font-mono text-[10px] text-neutral-500 mt-0.5 flex items-center gap-1">
                    v{s.current_version} · <StatusPill kind="status" value={v?.status || "offen"} sharp />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="col-span-12 md:col-span-9 bg-white p-5">
          {(creating || active) ? (
            <>
              {frozen && (
                <div className="mb-4 p-3 border border-neutral-300 bg-neutral-50 flex items-center gap-2 text-sm">
                  <Lock className="w-4 h-4" /> Set-Version v{activeVersion.version} ist <strong>{activeVersion.status}</strong> – eingefroren. Für Änderungen neue Version anlegen.
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Name</label>
                  <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} disabled={frozen} data-testid="prompt-set-name-input" className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm disabled:bg-neutral-100" />
                </div>
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Beschreibung</label>
                  <input value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} disabled={frozen} className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm disabled:bg-neutral-100" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Discovery-Pack-Version <span className="text-red-500">*</span></label>
                  <select
                    value={edit.discovery_pack_id && edit.discovery_pack_version !== "" ? `${edit.discovery_pack_id}::${edit.discovery_pack_version}` : ""}
                    onChange={(e) => {
                      const [pid, pv] = (e.target.value || "::").split("::");
                      setEdit({ ...edit, discovery_pack_id: pid, discovery_pack_version: pv });
                    }}
                    disabled={frozen}
                    data-testid="prompt-discovery-binding"
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono disabled:bg-neutral-100"
                  >
                    <option value="">— wählen —</option>
                    {closedDiscoveries.map((d) => <option key={d.pack_id} value={`${d.pack_id}::${d.version}`}>v{d.version} · {d.pack_id}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Einsatzkontext <span className="text-red-500">*</span></label>
                  <textarea value={edit.einsatzkontext} onChange={(e) => setEdit({ ...edit, einsatzkontext: e.target.value })} disabled={frozen} rows={2} data-testid="prompt-einsatzkontext" className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm disabled:bg-neutral-100" placeholder="Wofür ist dieses Set gedacht?" />
                </div>
              </div>

              <div className="space-y-3">
                {edit.prompts.map((p, i) => (
                  <div key={i} className="border border-neutral-200">
                    <div className="border-b border-neutral-200 px-3 py-2 grid grid-cols-12 gap-2 items-center bg-neutral-50">
                      <input placeholder="Name" value={p.name} onChange={(e) => updatePrompt(i, { name: e.target.value })} disabled={frozen} className="col-span-3 px-2 py-1 border border-neutral-300 rounded-sm text-xs disabled:bg-neutral-100" />
                      <select value={p.role} onChange={(e) => updatePrompt(i, { role: e.target.value })} disabled={frozen} data-testid={`prompt-${i}-role`} className="col-span-2 px-2 py-1 border border-neutral-300 rounded-sm text-xs disabled:bg-neutral-100">
                        {PROMPT_ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                      </select>
                      <input placeholder="provider" value={p.model_provider || ""} onChange={(e) => updatePrompt(i, { model_provider: e.target.value })} disabled={frozen} className="col-span-2 px-2 py-1 border border-neutral-300 rounded-sm text-xs font-mono disabled:bg-neutral-100" />
                      <input placeholder="model" value={p.model_name || ""} onChange={(e) => updatePrompt(i, { model_name: e.target.value })} disabled={frozen} className="col-span-3 px-2 py-1 border border-neutral-300 rounded-sm text-xs font-mono disabled:bg-neutral-100" />
                      <label className="col-span-1 text-[10px] flex items-center gap-1 font-mono">
                        <input type="checkbox" checked={p.active !== false} onChange={(e) => updatePrompt(i, { active: e.target.checked })} disabled={frozen} /> aktiv
                      </label>
                      <button onClick={() => removePrompt(i)} disabled={frozen} className="col-span-1 p-1 text-neutral-500 hover:text-red-600 disabled:opacity-30"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <input placeholder="Zweck (1 Satz)" value={p.purpose} onChange={(e) => updatePrompt(i, { purpose: e.target.value })} disabled={frozen} className="w-full px-3 py-1.5 text-xs border-b border-neutral-100 focus:outline-none disabled:bg-neutral-50" />
                    <textarea
                      rows={4}
                      value={p.content}
                      onChange={(e) => updatePrompt(i, { content: e.target.value })}
                      disabled={frozen}
                      data-testid={`prompt-${i}-content`}
                      placeholder="Prompt-Inhalt…"
                      className="w-full px-3 py-2 font-mono text-xs leading-relaxed focus:outline-none disabled:bg-neutral-50"
                    />
                  </div>
                ))}
                {!frozen && (
                  <button onClick={addPrompt} data-testid="prompts-add-prompt-button" className="text-xs text-[#183153] hover:underline">+ Prompt hinzufügen</button>
                )}
              </div>

              <div className="mt-5">
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Übergabe an Build <span className="text-red-500">*</span></label>
                <textarea value={edit.uebergabe_an_build} onChange={(e) => setEdit({ ...edit, uebergabe_an_build: e.target.value })} disabled={frozen} rows={3} data-testid="prompt-uebergabe" className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm disabled:bg-neutral-100" placeholder="Was muss ein Build-Verantwortlicher wissen, um diese Set-Version sinnvoll zu starten?" />
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-6 pt-4 border-t border-neutral-200">
                {!frozen && (
                  <>
                    <button onClick={() => save(false)} data-testid="prompt-save-button" className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">
                      <Save className="w-4 h-4" /> {creating ? "Anlegen" : "Aktuelle Version sichern"}
                    </button>
                    {!creating && (
                      <button onClick={() => save(true)} data-testid="prompt-version-button" className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded-sm" style={{ background: "#183153" }}>
                        <GitCommit className="w-4 h-4" /> Neue Version commit
                      </button>
                    )}
                    {!creating && activeVersion && (
                      <>
                        <button onClick={() => closeVersion("abgeschlossen")} data-testid="prompt-close-button" className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-emerald-300 text-emerald-800 rounded-sm hover:bg-emerald-50">
                          <CheckCircle2 className="w-4 h-4" /> v{activeVersion.version} abschließen
                        </button>
                        <button onClick={() => closeVersion("abgelehnt")} className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-red-300 text-red-700 rounded-sm hover:bg-red-50">
                          ablehnen
                        </button>
                      </>
                    )}
                  </>
                )}
                {!creating && (
                  <button onClick={remove} className="ml-auto inline-flex items-center gap-2 px-3 py-2 text-xs border border-red-200 text-red-700 rounded-sm hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" /> Löschen
                  </button>
                )}
              </div>

              {!creating && (active.versions.length > 1) && (
                <div className="mt-6">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Versionen</div>
                  <div className="border border-neutral-200">
                    {active.versions.slice().reverse().map((v) => (
                      <div key={v.version} className={`flex items-center justify-between px-4 py-2 border-b border-neutral-100 ${v.version === active.current_version ? "bg-neutral-50" : ""}`}>
                        <div className="font-mono text-xs">v{v.version} · {v.prompts.length} Prompts {v.discovery_pack_version != null && <span className="text-neutral-500">· Discovery v{v.discovery_pack_version}</span>}</div>
                        <div className="flex items-center gap-2">
                          <StatusPill kind="status" value={v.status || "offen"} sharp />
                          <span className="font-mono text-[10px] text-neutral-500">{fmtDate(v.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-neutral-500 py-12 text-center">
              {projectId ? "Wähle ein Set aus der Liste oder lege ein neues an." : "Bitte zuerst ein Projekt wählen."}
            </div>
          )}
        </div>
      </div>
    </InternalLayout>
  );
}
